import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { FileSpreadsheet, FileText, CalendarIcon, Download, Users, Clock, MapPin, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function ReportsPage() {
  const [workers, setWorkers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  
  const [reportType, setReportType] = useState("weekly");
  const [selectedWorker, setSelectedWorker] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [startDate, setStartDate] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [endDate, setEndDate] = useState(endOfWeek(new Date(), { weekStartsOn: 1 }));

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    // Update dates based on report type
    const now = new Date();
    if (reportType === "weekly") {
      setStartDate(startOfWeek(now, { weekStartsOn: 1 }));
      setEndDate(endOfWeek(now, { weekStartsOn: 1 }));
    } else {
      setStartDate(startOfMonth(now));
      setEndDate(endOfMonth(now));
    }
  }, [reportType]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchSummary();
    }
  }, [startDate, endDate, selectedWorker, selectedLocation]);

  const fetchFilters = async () => {
    try {
      const [workersRes, locationsRes] = await Promise.all([
        axios.get(`${API}/workers`),
        axios.get(`${API}/locations`)
      ]);
      setWorkers(workersRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd")
      });
      
      const response = await axios.get(`${API}/reports/workers-summary?${params}`);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const downloadReport = async (formatType) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/reports/generate`,
        {
          report_type: reportType,
          start_date: format(startDate, "yyyy-MM-dd"),
          end_date: format(endDate, "yyyy-MM-dd"),
          worker_id: selectedWorker !== "all" ? selectedWorker : null,
          location_id: selectedLocation !== "all" ? selectedLocation : null,
          format: formatType
        },
        { responseType: "blob" }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `relatorio_ponto_${format(startDate, "yyyy-MM-dd")}_${format(endDate, "yyyy-MM-dd")}.${formatType === "excel" ? "xlsx" : "pdf"}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Relatório ${formatType.toUpperCase()} baixado com sucesso!`);
    } catch (error) {
      toast.error("Erro ao gerar relatório");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const quickPeriods = [
    { label: "Esta Semana", action: () => {
      setReportType("weekly");
      setStartDate(startOfWeek(new Date(), { weekStartsOn: 1 }));
      setEndDate(endOfWeek(new Date(), { weekStartsOn: 1 }));
    }},
    { label: "Semana Passada", action: () => {
      setReportType("weekly");
      const lastWeek = subWeeks(new Date(), 1);
      setStartDate(startOfWeek(lastWeek, { weekStartsOn: 1 }));
      setEndDate(endOfWeek(lastWeek, { weekStartsOn: 1 }));
    }},
    { label: "Este Mês", action: () => {
      setReportType("monthly");
      setStartDate(startOfMonth(new Date()));
      setEndDate(endOfMonth(new Date()));
    }},
    { label: "Mês Passado", action: () => {
      setReportType("monthly");
      const lastMonth = subMonths(new Date(), 1);
      setStartDate(startOfMonth(lastMonth));
      setEndDate(endOfMonth(lastMonth));
    }}
  ];

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="reports-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-manrope">Relatórios</h1>
          <p className="text-gray-500 mt-1">Exporte relatórios de horas trabalhadas</p>
        </div>

        {/* Filters Card */}
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-gray-800">Configurar Relatório</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Quick Period Buttons */}
            <div>
              <Label className="text-gray-700 mb-3 block">Período Rápido</Label>
              <div className="flex flex-wrap gap-2">
                {quickPeriods.map((period) => (
                  <Button
                    key={period.label}
                    variant="outline"
                    size="sm"
                    onClick={period.action}
                    className="border-gray-200 text-gray-600 hover:bg-amber-50 hover:border-[#F5A623] hover:text-[#F5A623]"
                    data-testid={`quick-period-${period.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {period.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Data Início</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start border-gray-200 text-gray-700 bg-white hover:bg-gray-50" data-testid="start-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(startDate, "dd/MM/yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-gray-200" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setStartDate(date)}
                      locale={ptBR}
                      className="bg-white rounded-md"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Data Fim</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start border-gray-200 text-gray-700 bg-white hover:bg-gray-50" data-testid="end-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(endDate, "dd/MM/yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-gray-200" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => date && setEndDate(date)}
                      locale={ptBR}
                      className="bg-white rounded-md"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Trabalhador</Label>
                <Select value={selectedWorker} onValueChange={setSelectedWorker}>
                  <SelectTrigger className="bg-white border-gray-200 text-gray-700" data-testid="worker-filter">
                    <Users className="mr-2 h-4 w-4 text-gray-400" />
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">Todos os trabalhadores</SelectItem>
                    {workers.map((worker) => (
                      <SelectItem key={worker.id} value={worker.id}>
                        {worker.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Local</Label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger className="bg-white border-gray-200 text-gray-700" data-testid="location-filter">
                    <MapPin className="mr-2 h-4 w-4 text-gray-400" />
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">Todos os locais</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-100">
              <Button
                onClick={() => downloadReport("excel")}
                disabled={loading}
                className="flex-1 bg-[#2ECC40] hover:bg-[#27B038] text-white font-semibold"
                data-testid="download-excel-btn"
              >
                {loading ? (
                  <div className="spinner w-5 h-5 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <FileSpreadsheet className="mr-2 h-5 w-5" />
                    Baixar Excel
                  </>
                )}
              </Button>
              <Button
                onClick={() => downloadReport("pdf")}
                disabled={loading}
                className="flex-1 btn-primary text-white font-semibold"
                data-testid="download-pdf-btn"
              >
                {loading ? (
                  <div className="spinner w-5 h-5 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <FileText className="mr-2 h-5 w-5" />
                    Baixar PDF
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Preview */}
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#F5A623]" />
              Resumo do Período
              {summary && (
                <span className="text-sm font-normal text-gray-500">
                  ({format(startDate, "dd/MM")} - {format(endDate, "dd/MM/yyyy")})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : summary && summary.workers.length > 0 ? (
              <div className="space-y-6">
                {/* Total */}
                <div className="bg-gradient-to-r from-green-50 to-amber-50 rounded-lg p-6 text-center">
                  <p className="text-sm text-gray-600">Total de Horas no Período</p>
                  <p className="text-4xl font-bold gradient-text mt-2">{summary.total_hours}</p>
                </div>

                {/* Workers Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Trabalhador</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Horas</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Entradas</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Saídas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.workers.map((worker) => (
                        <tr key={worker.worker_id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#2ECC40] to-[#F5A623] flex items-center justify-center text-white text-sm font-bold">
                                {worker.worker_name.charAt(0).toUpperCase()}
                              </div>
                              <span className="font-medium text-gray-800">{worker.worker_name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="font-semibold text-[#F5A623]">{worker.total_hours}</span>
                          </td>
                          <td className="py-3 px-4 text-center text-gray-600">{worker.entries}</td>
                          <td className="py-3 px-4 text-center text-gray-600">{worker.exits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Nenhum registro encontrado para este período</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
